/** 
 * Usually css-variable should always be used with its babel plugin
 * 
 * However in some scenarios e.g. storybook / jest it might be difficult
 * to setup.
 * For those cases this counter provides a very basic fallback to generate 
 * different ids.
 */
let fallbackId = 9 ** 9;
export class CSSVariable extends // Inherit from String to be compatible to most CSS-in-JS solutions
// Hacky cast to any for reduced autocomplete
String {
  /** Name e.g. `--baseSize` */

  /** Value e.g. `var(--baseSize, 12px)` */

  /** 
   * Creates a new CSS Variable with a unique autogenerated name 
   * 
   * E.g. `var(--1isaui4-0)`
   */

  /** 
   * Creates a new CSS Variable with a custom defined name 
   * 
   * E.g. `var(--baseSize)`
   */

  /** 
   * Creates a new CSS Variable with a unique autogenerated name
   * and a fallback value
   * 
   * E.g. `var(--1isaui4-0, 12px)`
   */

  /** 
   * Creates a new CSS Variable with a unique autogenerated name
   * and a fallback value
   * 
   * E.g. `var(--baseSize, 12px)`
   */

  /*#__PURE__*/
  constructor(...args) {
    const optionArg = args.find(arg => typeof arg === "object");
    const name = "--" + (args.filter(arg => typeof arg === "string").join('-').toLowerCase() || // Fallback if babel plugin is missing
    (fallbackId++).toString(16));
    const val = `var(${name}${optionArg ? `, ${optionArg.value}` : ""})`;
    super(val);
    this.val = val;
    this.name = name;
  }
  /** Returns the variable name e.g. `--baseSize` */


  getName() {
    return this.name;
  }
  /** Create a CSS Object e.g. `{ "--baseSize": '12px' }` */


  toStyle(newValue) {
    return {
      [this.name]: `${newValue}`
    };
  }
  /** Create a CSS String e.g. `--baseSize:12px;` */


  toCSS(newValue) {
    return `${this.name}:${newValue};`;
  }

}
export const createVar = (...args) => new CSSVariable(...args);
/**
 * A theme structure groups multiple CSSVariable instances 
 * in a nested object structure e.g.:
 * 
 * ```ts
 * const theme = { 
 *   colors: {
 *     primary: createVar(),
 *     secondary: createVar()
 *   },
 *   spacings: {
 *     small: createVar(),
 *     large: createVar()
 *   }
 * }
 * ```
 */

/**
 * Assign multiple CSSVariables for a given flat or nested Theme Contract
 * 
 * @example
 * ```js
 * const theme = {
 *   colors: {
 *    primary: createVar(),
 *    secondary: createVar(),
 *  }
 * }
 * 
 * const brightThemeCSS = assignVars(theme, {
 *   colors: {
 *    primary: "#6290C3",
 *  }
 * }) 
 * 
 * console.log(brightThemeCSS) // -> `--1isaui4-0:#6290C3;`
 * ```
 */
export const assignVars = (cssVariables, cssVariableValues) => Object.keys(cssVariableValues).map(key => typeof cssVariableValues[key] === "string" ? cssVariables[key].toCSS(cssVariableValues[key]) : assignVars(cssVariables[key], cssVariableValues[key])).join("");
/**
 * Serialize all CSS Variable values for an entire nested or flat Theme Contract
 * 
 * @example
 * ```js
 * const theme = {
 *   colors: {
 *    primary: createVar(),
 *    secondary: createVar(),
 *  }
 * }
 * 
 * const brightThemeCSS = createGlobalTheme(":root", theme, {
 *   colors: {
 *    primary: "#6290C3",
 *    secondary: "#C2E7DA",
 *  }
 * }) 
 * 
 * console.log(brightThemeCSS) // -> `:root { --1isaui4-0:#6290C3; --1isaui4-1:#C2E7DA; }`
 * ```
 */

export const createGlobalTheme = (scope, cssVariables, cssVariableValues) => `${scope ? `${scope}{` : ''}${assignVars(cssVariables, cssVariableValues)}${scope ? '}' : ''}`;
